import { useCallback, useEffect, useState } from 'react';
import { Input, InputProps, Stack, Typography } from '@mui/material';
import { FileRejection, useDropzone } from 'react-dropzone';
import { MdUpload } from 'react-icons/md';
import { OutlinedButton } from '@bymason-private/pattern-components';
import { pxToRem, pxToLineHeight, getErrorMessage } from '@bymason/utils';
import { ArtifactModel, artifactsClient, ArtifactCreationType } from '@bymason/controller-api';
import { useAuth } from '@bymason-private/auth-provider';

export type FileFieldMimeType = '.apk' | '.zip' | '.jpeg' | 'jpg' | '.png' | '.yaml' | '.yml';

export interface FileUploadButtonProps
  extends Omit<
    InputProps,
    'accept' | 'disableUnderline' | 'error' | 'fullWidth' | 'onChange' | 'sx' | 'type' | 'buildFiles'
  > {
  accept?: FileFieldMimeType[];
  assetType: ArtifactCreationType;
  buttonText: string;
  errorText?: string;
  disabled?: boolean;
  onFileRejected?: (error: string) => void;
  onFileSelected?: (file: ArtifactModel) => void;
  onFileUploadBusy?: (busy: boolean) => void;
  placeholder?: string;
}

export function FileUploadButton({
  accept,
  assetType,
  buttonText,
  errorText,
  disabled,
  placeholder,
  onFileRejected,
  onFileSelected,
  onFileUploadBusy,
  ...inputProps
}: FileUploadButtonProps) {
  const { authToken = '' } = useAuth();
  const [uploading, setUploading] = useState(false);

  const registerArtifact = useCallback(
    async (fileToUpload: File) => {
      if (authToken) {
        let artifact: ArtifactModel | null = null;
        try {
          artifact =
            (await artifactsClient.createArtifact(
              {
                file: fileToUpload,
                fileName: fileToUpload.name,
                type: assetType,
              },
              authToken
            )) || null;
        } catch (err) {
          const message = getErrorMessage(err);
          onFileRejected && onFileRejected(message);
        }
        return artifact;
      }
    },
    [assetType, authToken, onFileRejected]
  );

  const handleDrop = useCallback(
    async <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (onFileSelected && acceptedFiles.length) {
        const acceptedFile = acceptedFiles[0];

        setUploading(true);
        const registered = await registerArtifact(acceptedFile);
        if (registered) {
          onFileSelected(registered);
        }
        setUploading(false);
      } else if (fileRejections.length && onFileRejected) {
        onFileRejected(fileRejections[0].errors[0].message);
      }
    },
    [onFileRejected, onFileSelected, registerArtifact]
  );

  useEffect(() => {
    onFileUploadBusy && onFileUploadBusy(uploading);
  }, [onFileUploadBusy, uploading]);

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    disabled,
    maxFiles: 1,
    onDrop: handleDrop,
  });

  return (
    <Stack direction={'column'} spacing={0.5} alignItems='center' width='100%'>
      <OutlinedButton
        {...getRootProps()}
        disabled={disabled || uploading}
        startIcon={<MdUpload />}
        fullWidth
        sx={{
          borderColor: 'primary.main',
          borderSize: 1,
          color: errorText ? 'error.main' : undefined,
          fontSize: pxToRem(13),
          fontWeight: 500,
          lineHeight: pxToLineHeight(13, 22),
          px: 1.5,
          py: 0.5,
        }}
      >
        <Input inputProps={getInputProps()} {...inputProps} type='file' />
        {buttonText}
      </OutlinedButton>
      {!!placeholder && (
        <Typography
          sx={{
            color: 'secondary.main',
            fontSize: pxToRem(12),
            fontWeight: 400,
            lineHeight: pxToLineHeight(12, 21),
            opacity: 0.87,
          }}
        >
          {placeholder}
        </Typography>
      )}
    </Stack>
  );
}
